import auraAvatar from '@/assets/images/users/aura-avatar.png';
import userAvatar from '@/assets/images/users/avatar-2.jpg';
import React, { useEffect, useRef } from 'react';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { Interweave } from 'interweave';
import { uniqueId } from 'react-bootstrap-typeahead/types/utils';
import { useAuraContext } from '@/common';
import { AuraMessage as AuraMessageType } from '@/common/context/auraUtils/aura.types';

export default function AuraChat() {
	const { messages, pushMessage, setNewMessage, newMessage, sendNewMessage } = useAuraContext();

	const { toggleOpen } = useAuraContext();

	const chatContainerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		chatContainerRef.current = document.getElementById('chat-container') as HTMLDivElement;
	}, []);

	useEffect(() => {
		if (chatContainerRef.current && messages.length > 1) {
			const lastMessage = chatContainerRef.current.lastElementChild;
			if (lastMessage) {
				lastMessage.scrollIntoView({ behavior: 'smooth' });
			}
		}
	}, [messages]);

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		if (newMessage.trim() === '') return;
		e.preventDefault();

		const newMsg: AuraMessageType = {
			id: uniqueId(),
			message: newMessage,
			type: 'user',
			date: new Date(),
		};
		pushMessage(newMsg);
		sendNewMessage();
		setNewMessage('');
	};

	return (
		<>
			<Card className="max-w-25 max-h-50 shadow-lg elevation-5">
				<Card.Header>
					<Container>
						{/* Add Avatar to the left, name in the center */}

						<Row className="align-items-center">
							<Col xs="auto">
								<img
									src={auraAvatar}
									alt=""
									className="img-fluid avatar-sm rounded"
								/>
							</Col>
							<Col xs="auto">
								<h3 className="mb-0">Aura</h3>
							</Col>
							<Col className="text-end">
								<Button variant="danger" size="sm" onClick={() => toggleOpen()}>
									<i className="mdi mdi-close"></i>
								</Button>
							</Col>
						</Row>
					</Container>
				</Card.Header>

				<Card.Body
					style={{
						overflowY: 'auto',
						minHeight: '40.5vh',
						maxHeight: '40.5vh',
						maxWidth: '25vw',
						minWidth: '25vw',
					}}
				>
					<div id="chat-container" className="chat-container p-3">
						{messages.map((msg) => {
							if (msg.type === 'aura')
								return <AuraMessage key={msg.id}>{msg.message}</AuraMessage>;
							if (msg.type === 'user')
								return <UserMessage key={msg.id}>{msg.message}</UserMessage>;
						})}
					</div>
				</Card.Body>

				<Card.Footer>
					<MessageForm
						handleSubmit={handleSubmit}
						setMessage={setNewMessage}
						message={newMessage}
					/>
				</Card.Footer>
			</Card>
		</>
	);
}

interface MessageFormProps {
	handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
	setMessage: (message: string) => void;
	message: string;
}

function MessageForm({ handleSubmit, setMessage, message }: MessageFormProps) {
	return (
		<>
			<Form
				className="d-flex"
				onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
					e.preventDefault();
					handleSubmit(e);
				}}
			>
				<Form.Control
					onChange={(e) => {
						setMessage(e.target.value);
					}}
					value={message}
					type="text"
					placeholder="Escribe tu mensaje..."
					className="me-2"
				/>
				<Button variant="primary" type="button" formAction="submit">
					Enviar
				</Button>
			</Form>
		</>
	);
}

interface AuraMessageProps {
	children: React.ReactNode;
}

function AuraMessage({ children }: AuraMessageProps) {
	return (
		<div className="d-flex align-items-start mb-4">
			<img
				src={auraAvatar}
				className="rounded-circle me-2"
				width="36"
				height="36"
				alt="Aura"
			/>
			<div className="flex-grow-1">
				<div className="bg-light rounded p-3">
					<Interweave content={children as string} />
				</div>
			</div>
		</div>
	);
}

interface UserMessageProps {
	children: React.ReactNode;
}

function UserMessage({ children }: UserMessageProps) {
	return (
		<div className="d-flex align-items-start mb-4 justify-content-end">
			<div className="flex-grow-1">
				<div className="bg-primary text-white rounded p-3">{children}</div>
			</div>
			<img
				src={userAvatar}
				className="rounded-circle ms-2"
				width="36"
				height="36"
				alt="User"
			/>
		</div>
	);
}
